/**
 * @fileoverview Alternative admin authentication service for Gmail users.
 * 
 * This service allows existing Gmail users to access admin panel
 * by checking their email against a whitelist.
 */

import { auth } from '../firebase/config';
import { signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged, User } from 'firebase/auth';
import rateLimitService from './rateLimitService';
import { 
  getCurrentAdminConfig, 
  getAllAdminEmails, 
  isAdminInEnvironment, 
  getUserRole 
} from '../config/adminConfig';

interface AdminUser {
  uid: string;
  email: string;
  isAdmin: boolean;
  lastLogin: Date;
}

interface AdminSession {
  user: AdminUser;
  token: string;
  expiresAt: Date;
  ipAddress: string;
}

class AdminAuthServiceGmail {
  private readonly SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours
  private currentSession: AdminSession | null = null;

  /**
   * Authenticate admin user with Gmail
   */
  async authenticateAdminWithGmail(ipAddress: string): Promise<{ success: boolean; message: string; user?: AdminUser }> {
    // Check rate limiting
    const rateLimitCheck = rateLimitService.isAllowed('admin_auth', ipAddress);
    if (!rateLimitCheck.allowed) {
      return {
        success: false,
        message: `Too many login attempts. Try again in ${Math.ceil((rateLimitCheck.resetTime - Date.now()) / 60000)} minutes.`
      };
    }

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      
      // Sign in with Google
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user email is authorized for current environment
      if (!isAdminInEnvironment(user.email!)) {
        await signOut(auth);
        const { getEnvironment } = await import('../config/environment');
        const environment = getEnvironment();
        return {
          success: false,
          message: `Access denied. Your email is not authorized for admin access in ${environment} environment.`
        };
      }

      // Get user role
      const role = getUserRole(user.email!);
      const { getEnvironment } = await import('../config/environment');
      const environment = getEnvironment();

      // Create admin session
      const adminUser: AdminUser = {
        uid: user.uid,
        email: user.email!,
        isAdmin: true,
        lastLogin: new Date()
      };

      const session: AdminSession = {
        user: adminUser,
        token: await user.getIdToken(),
        expiresAt: new Date(Date.now() + this.SESSION_DURATION),
        ipAddress
      };

      this.currentSession = session;

      // Log admin login
      this.logAdminAction('LOGIN', adminUser.email, ipAddress, { role, environment });

      return {
        success: true,
        message: `Authentication successful. Welcome ${role} admin to ${environment} environment.`,
        user: adminUser
      };

    } catch (error: any) {
      rateLimitService.recordRequest('admin_auth', ipAddress);
      return {
        success: false,
        message: `Authentication failed: ${error.message}`
      };
    }
  }

  /**
   * Validate current admin session
   */
  async validateSession(token: string, ipAddress: string): Promise<{ valid: boolean; user?: AdminUser; message?: string }> {
    if (!this.currentSession) {
      return { valid: false, message: 'No active session.' };
    }

    if (this.currentSession.token !== token) {
      return { valid: false, message: 'Invalid session token.' };
    }

    if (Date.now() > this.currentSession.expiresAt.getTime()) {
      this.logout();
      return { valid: false, message: 'Session expired.' };
    }

    if (this.currentSession.ipAddress !== ipAddress) {
      this.logout();
      return { valid: false, message: 'Session IP mismatch.' };
    }

    return { valid: true, user: this.currentSession.user };
  }

  /**
   * Logout admin user
   */
  async logout(): Promise<void> {
    if (this.currentSession) {
      this.logAdminAction('LOGOUT', this.currentSession.user.email, this.currentSession.ipAddress);
    }
    
    this.currentSession = null;
    await signOut(auth);
  }

  /**
   * Get current admin user
   */
  getCurrentUser(): AdminUser | null {
    return this.currentSession?.user || null;
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.currentSession?.user?.isAdmin || false;
  }

  /**
   * Check if email is in admin whitelist
   */
  isEmailWhitelisted(email: string): boolean {
    return getAllAdminEmails().includes(email);
  }

  /**
   * Log admin actions for audit
   */
  private logAdminAction(action: string, email: string, ipAddress: string, context?: any): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      email,
      ipAddress,
      userAgent: navigator.userAgent,
      context
    };

    console.log('🔐 Admin Action:', logEntry);
  }

  /**
   * Get client IP address (simplified)
   */
  getClientIP(): string {
    return '127.0.0.1'; // Placeholder
  }
}

export const adminAuthServiceGmail = new AdminAuthServiceGmail();
export default adminAuthServiceGmail; 